"use client";

import { MentionContent, MentionItem, MentionLabel } from "@/components/ui/mention";
import { useAiPersonasQuery } from "@/features/ai/api/ai-hooks";
import { ProfileAvatar } from "@/features/avatar";
import { getInitials } from "@/features/avatar/avatar.utils";
import { useChatRoomsByContainerQuery } from "@/features/chat/api/chat-hooks";
import { useContainerMembersQuery } from "@/features/containers/api/containers-hooks";
import { useDocumentsByContainerQuery } from "@/features/docs/api/docs-hooks";
import { AVATARS_STORAGE_BUCKET } from "@/features/profiles";
import { getDisplayName } from "@/features/profiles/profile.utils";
import { cn } from "@/lib/utils";
import type {
  AiPersona,
  ChangeRequest,
  Container,
  InvitedMember,
  UserMember,
} from "@shared/types/supabase.types";
import { FileText, GitBranch, MessageCircle } from "lucide-react";

// ============================================================================
// Shared hook
// ============================================================================

/**
 * @param containerId - the container the mention picker belongs to (AI personas
 *   are always read from it).
 * @param projectContainerId - parent project, used for the docs/chats links.
 * @param fetchContainerLinks - whether to fetch those docs/chats links.
 * @param membersContainerId - CR-107: container whose human members are the
 *   mention candidates. Defaults to `containerId`. A room shared with the
 *   project has no `container_members` rows for its audience, so its callers
 *   pass the parent project instead — see `resolveMentionMembersContainerId`.
 */
export function useContainerMentionData(
  containerId: string,
  projectContainerId?: string,
  fetchContainerLinks = false,
  membersContainerId?: string,
) {
  const resourceContainerId = projectContainerId ?? containerId;
  const { data: aiPersonas } = useAiPersonasQuery({ containerId });
  const { data: members } = useContainerMembersQuery(membersContainerId ?? containerId, ["user"]);
  const { data: docs } = useDocumentsByContainerQuery(
    fetchContainerLinks ? resourceContainerId : undefined,
  );
  const { data: chats } = useChatRoomsByContainerQuery(
    fetchContainerLinks ? resourceContainerId : undefined,
  );
  return { aiPersonas, members, docs, chats };
}

// ============================================================================
// Shared row components (used by both MentionMembersContent and
// the tiptap MentionCommandsList)
// ============================================================================

interface MentionPersonaRowProps {
  persona: AiPersona;
}

export function MentionPersonaRow({ persona }: MentionPersonaRowProps) {
  return (
    <div className="flex gap-2">
      <ProfileAvatar
        avatarPath={persona.avatar_url}
        fallbackInitials={getInitials({ fullName: persona.display_name })}
        size={32}
        storageName={AVATARS_STORAGE_BUCKET}
      />
      <div className="flex flex-col">
        <span className="text-sm font-medium">{persona.display_name}</span>
        <span className="text-muted-foreground text-xs">{persona.handle}</span>
      </div>
    </div>
  );
}

interface MentionMemberRowProps {
  member: UserMember;
  /** Avatar size in px. Defaults to 32. */
  avatarSize?: number;
}

export function MentionMemberRow({ member, avatarSize = 32 }: MentionMemberRowProps) {
  const hasName = !!member.profile_first_name?.trim() || !!member.profile_last_name?.trim();
  const displayName = getDisplayName({
    firstName: member.profile_first_name,
    lastName: member.profile_last_name,
    email: member.profile_email,
  });

  return (
    <div className="flex gap-2">
      <ProfileAvatar
        avatarPath={member.profile_avatar_url || null}
        fallbackInitials={getInitials({
          firstName: member.profile_first_name,
          lastName: member.profile_last_name,
          email: member.profile_email,
        })}
        size={avatarSize}
        storageName={AVATARS_STORAGE_BUCKET}
      />
      <div className={cn("flex", hasName ? "flex-col" : "items-center")}>
        <span className="text-sm font-medium">{displayName}</span>
        {!!hasName && <span className="text-muted-foreground text-xs">{member.profile_email}</span>}
      </div>
    </div>
  );
}

interface MentionInvitedMemberRowProps {
  member: InvitedMember;
  /** Avatar size in px. Defaults to 32. */
  avatarSize?: number;
}

export function MentionInvitedMemberRow({ member, avatarSize = 32 }: MentionInvitedMemberRowProps) {
  return (
    <div className="flex gap-2">
      <ProfileAvatar
        avatarPath={null}
        fallbackInitials={getInitials({
          email: member.invite_email ?? undefined,
        })}
        size={avatarSize}
        storageName={AVATARS_STORAGE_BUCKET}
      />
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">{member.invite_email}</span>
        <span className="text-muted-foreground text-xs">(pending)</span>
      </div>
    </div>
  );
}

interface MentionDocRowProps {
  doc: Container;
}

export function MentionDocRow({ doc }: MentionDocRowProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-amber-100 dark:bg-amber-900/30">
        <FileText className="h-4 w-4 text-amber-700 dark:text-amber-400" />
      </div>
      <span className="text-sm font-medium">{doc.name}</span>
    </div>
  );
}

interface MentionChatRowProps {
  chat: Container;
}

export function MentionChatRow({ chat }: MentionChatRowProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-emerald-100 dark:bg-emerald-900/30">
        <MessageCircle className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
      </div>
      <span className="text-sm font-medium">{chat.name}</span>
    </div>
  );
}

interface MentionCrRowProps {
  cr: ChangeRequest;
}

export function MentionCrRow({ cr }: MentionCrRowProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-violet-100 dark:bg-violet-900/30">
        <GitBranch className="h-4 w-4 text-violet-700 dark:text-violet-400" />
      </div>
      <div className="flex flex-col">
        <span className="text-muted-foreground text-xs">CR-{cr.sequential_id}</span>
        <span className="text-sm font-medium">{cr.name}</span>
      </div>
    </div>
  );
}

// ============================================================================
// MentionMembersContent
// ============================================================================

interface MentionMembersContentProps {
  containerId: string;
  /** Project container ID for fetching docs/chats (when containerId is a sub-container like a chat room) */
  projectContainerId?: string;
  /**
   * CR-107: container whose human members are the mention candidates. Defaults
   * to `containerId`. Rooms shared with the project pass the parent project
   * here, because a shared room holds no `container_members` row for its
   * audience. AI personas, docs and chats keep reading `containerId` /
   * `projectContainerId`.
   */
  membersContainerId?: string;
  /** Exclude the member whose actor_id matches (comment threads) */
  excludeActorId?: string;
  /** Exclude the member whose user_id matches (chat) */
  excludeUserId?: string;
  membersLabel?: string;
  className?: string;
  /** Whether to show docs and chats in the mention list. Defaults to false. */
  showContainerLinks?: boolean;
}

export function MentionMembersContent({
  containerId,
  projectContainerId,
  membersContainerId,
  excludeActorId,
  excludeUserId,
  membersLabel = "Members",
  className,
  showContainerLinks = false,
}: MentionMembersContentProps) {
  const {
    aiPersonas,
    members: allMembers,
    docs,
    chats,
  } = useContainerMentionData(
    containerId,
    projectContainerId,
    showContainerLinks,
    membersContainerId,
  );

  const members = allMembers?.filter((m) => {
    if (excludeActorId && m.actor_id === excludeActorId) return false;
    if (excludeUserId && m.user_id === excludeUserId) return false;
    return true;
  });

  return (
    <MentionContent className={cn("max-h-56 overflow-y-auto", className)}>
      <MentionLabel>{membersLabel}</MentionLabel>
      {!members?.length ? (
        <MentionItem value="no-members" disabled>
          No members available
        </MentionItem>
      ) : (
        members.map((member) => {
          const displayName = getDisplayName({
            firstName: member.profile_first_name,
            lastName: member.profile_last_name,
            email: member.profile_email,
          });
          return (
            <MentionItem key={member.actor_id} value={displayName} label={displayName}>
              <MentionMemberRow member={member} />
            </MentionItem>
          );
        })
      )}
      {!!aiPersonas?.length && (
        <>
          <MentionLabel>AI assistants</MentionLabel>
          {aiPersonas.map((persona) => (
            <MentionItem
              key={persona.id}
              value={persona.handle}
              label={persona.handle.substring(1)}
            >
              <MentionPersonaRow persona={persona} />
            </MentionItem>
          ))}
        </>
      )}
      {showContainerLinks && !!docs?.length ? (
        <>
          <MentionLabel>Documents</MentionLabel>
          {docs.map((doc) => (
            <MentionItem key={doc.id} value={doc.name} label={doc.name}>
              <MentionDocRow doc={doc} />
            </MentionItem>
          ))}
        </>
      ) : null}
      {showContainerLinks && !!chats?.length ? (
        <>
          <MentionLabel>Chats</MentionLabel>
          {chats.map((chat) => (
            <MentionItem key={chat.id} value={chat.name} label={chat.name}>
              <MentionChatRow chat={chat} />
            </MentionItem>
          ))}
        </>
      ) : null}
    </MentionContent>
  );
}
